import { useMemo } from "react";

import {
  type GetCurrentAgenticSettingsEntitiesQuery,
  useAdStudioTemplatesQuery,
  useGetCurrentAgenticSettingsEntitiesQuery,
  useStyleEntitiesQuery,
} from "src/graphql";
import { AgentScope } from "src/graphql/types";
import { isAdTemplateOffered } from "src/pages/ad-studio/components/is-ad-template-offered";
import { isStyleOffered } from "src/pages/ad-studio/components/is-style-offered";
import type { StyleSelection } from "src/pages/ad-studio/components/ad-studio-style-select";
import { SKILLS_QUERY_VARIABLES } from "src/pages/context-hub/context-entities/surface-hooks";
import type { StyleRow } from "src/pages/context-hub/styles/filter-styles";
import {
  type AdStudioTemplateCard,
  isAdStudioTemplateCard,
} from "src/pages/ad-studio/templates/types";
import { useStableWhileEqual } from "src/hooks/use-stable-while-equal";
import type { ComposerSelections } from "src/pages/projects/amp-composer/composer-selections";
import type { SelectedEmailAsset } from "src/components/llm/component-picker";
import {
  type BrandScope,
  filterToBrandScope,
  isInBrandScope,
  liveBrandIds,
  narrowBrandScope,
} from "src/utils/brand-scope";

const AD_SURFACES = [AgentScope.Ads];
const EMAIL_SURFACES = [AgentScope.Lcm];

/** An option with the live brand profiles it belongs to; empty means every brand. */
type Branded<T> = T & { brandProfileIds: string[] };

type SkillRow =
  GetCurrentAgenticSettingsEntitiesQuery["getCurrentAgenticSettingsEntities"][number];

/** Every style, ad template, and skill the workspace has, before any filtering. */
export type LoadedOptions = {
  styles: Branded<StyleRow>[];
  adTemplates: Branded<AdStudioTemplateCard>[];
  skills: Branded<SkillRow>[];
};

/**
 * What each picker can offer right now: eligible, and within the brand scope.
 * Email templates and components are not here: the email asset modal searches
 * them itself, and the composer holds only the picked ones in its selections.
 */
export type ComposerOptions = {
  adStyles: Branded<StyleRow>[];
  emailStyles: Branded<StyleRow>[];
  adTemplates: Branded<AdStudioTemplateCard>[];
  skills: Branded<SkillRow>[];
};

/** A style choice as the composer shows and sends it; a picked style no longer offered reads as Auto. */
export type SelectedStyle =
  | { kind: "auto" }
  | { kind: "none" }
  | { kind: "style"; style: Branded<StyleRow> };

/**
 * The composer's selections resolved against its options: what the controls
 * show and what the next message sends. A pick the options no longer offer,
 * or an email asset outside the brand scope, is left out.
 */
export type SelectedOptions = Pick<
  ComposerSelections,
  "creativeBoost" | "useVideoEngine"
> & {
  adsStyle: SelectedStyle;
  emailStyle: SelectedStyle;
  adTemplates: Branded<AdStudioTemplateCard>[];
  emailTemplates: SelectedEmailAsset[];
  emailComponents: SelectedEmailAsset[];
  skills: Branded<SkillRow>[];
};

/** The channels the person can make, from their entitlements and flags. */
export type ComposerChannels = { ad: boolean; email: boolean };

/**
 * The options the composer's pickers offer, loaded once for every channel the
 * person can make, and the brand scope that limits them.
 */
export function useComposerOptions({
  channels,
  selections,
  pageBrandProfileId,
}: {
  channels: ComposerChannels;
  selections: ComposerSelections;
  /** The brand of the page beside the chat. */
  pageBrandProfileId: string | undefined;
}): {
  options: ComposerOptions;
  selectedOptions: SelectedOptions;
  brandScope: BrandScope;
  isLoading: boolean;
} {
  const styles = useStyleEntitiesQuery(undefined, {
    enabled: channels.ad || channels.email,
  });
  const adTemplates = useAdStudioTemplatesQuery(undefined, {
    enabled: channels.ad,
  });
  const skills = useGetCurrentAgenticSettingsEntitiesQuery(
    SKILLS_QUERY_VARIABLES,
  );
  const loaded = useMemo(
    (): LoadedOptions => ({
      styles: (styles.data?.styleEntities ?? []).map((style) => ({
        ...style,
        brandProfileIds: style.brandProfiles.map(({ id }) => id),
      })),
      adTemplates: (adTemplates.data?.getCurrentAgenticSettingsEntities ?? [])
        .filter(isAdStudioTemplateCard)
        .map((template) => ({
          ...template,
          brandProfileIds: liveBrandIds(template.brandProfileAssignments),
        })),
      skills: (skills.data?.getCurrentAgenticSettingsEntities ?? []).map(
        (skill) => ({
          ...skill,
          brandProfileIds: liveBrandIds(skill.brandProfileAssignments),
        }),
      ),
    }),
    [styles.data, adTemplates.data, skills.data],
  );
  const brandScope = useStableWhileEqual(
    composerBrandScope(loaded, selections, { pageBrandProfileId, channels }),
  );
  const { adsStyle, emailStyle } = selections;
  const options = useMemo(
    () => availableOptions(loaded, { brandScope, adsStyle, emailStyle }),
    [loaded, brandScope, adsStyle, emailStyle],
  );
  const selectedOptions = useMemo(
    () => selectedOptionsFor(selections, options, brandScope),
    [selections, options, brandScope],
  );
  return {
    options,
    selectedOptions,
    brandScope,
    isLoading: styles.isLoading || adTemplates.isLoading || skills.isLoading,
  };
}

/** Resolves the selections against the options; the one place a pick is dropped for being out of scope or unavailable. */
export function selectedOptionsFor(
  selections: ComposerSelections,
  options: ComposerOptions,
  brandScope: BrandScope,
): SelectedOptions {
  const pickedFrom = <T extends { id: string }>(
    ids: readonly string[],
    list: readonly T[],
  ) => ids.flatMap((id) => list.find((option) => option.id === id) ?? []);
  const styleFrom = (
    selection: StyleSelection,
    list: readonly Branded<StyleRow>[],
  ): SelectedStyle => {
    if (selection.kind !== "style") {
      return selection;
    }
    const style = list.find((option) => option.id === selection.id);
    return style ? { kind: "style", style } : { kind: "auto" };
  };
  return {
    adsStyle: styleFrom(selections.adsStyle, options.adStyles),
    emailStyle: styleFrom(selections.emailStyle, options.emailStyles),
    creativeBoost: selections.creativeBoost,
    useVideoEngine: selections.useVideoEngine,
    adTemplates: pickedFrom(selections.adTemplateIds, options.adTemplates),
    // The email asset modal searches its own library, so the options do not hold these.
    emailTemplates: filterToBrandScope(selections.emailTemplates, brandScope),
    emailComponents: filterToBrandScope(selections.emailComponents, brandScope),
    skills: pickedFrom(selections.skillIds, options.skills),
  };
}

/** The style selection a picker shows for a resolved style. */
export function styleSelectionOf(selected: SelectedStyle): StyleSelection {
  return selected.kind === "style"
    ? { kind: "style", id: selected.style.id }
    : selected;
}

/**
 * The brands the composer's options are limited to: the page's brand, narrowed
 * by every brand-specific pick on a channel the person can make.
 */
export function composerBrandScope(
  loaded: LoadedOptions,
  selections: ComposerSelections,
  {
    pageBrandProfileId,
    channels,
  }: { pageBrandProfileId: string | undefined; channels: ComposerChannels },
): BrandScope {
  const brandsOf = (
    list: readonly { id: string; brandProfileIds: string[] }[],
    id: string,
  ) => list.find((option) => option.id === id)?.brandProfileIds ?? [];
  const styleBrands = (selection: StyleSelection) =>
    selection.kind === "style" ? [brandsOf(loaded.styles, selection.id)] : [];
  return narrowBrandScope(pageBrandProfileId ? [pageBrandProfileId] : null, [
    ...(channels.ad
      ? [
          ...styleBrands(selections.adsStyle),
          ...selections.adTemplateIds.map((id) =>
            brandsOf(loaded.adTemplates, id),
          ),
        ]
      : []),
    ...(channels.email
      ? [
          ...styleBrands(selections.emailStyle),
          ...[...selections.emailTemplates, ...selections.emailComponents].map(
            (asset) => asset.brandProfileIds ?? [],
          ),
        ]
      : []),
    ...selections.skillIds.map((id) => brandsOf(loaded.skills, id)),
  ]);
}

/** The loaded options each picker offers within the brand scope, newest first where the picker sorts. */
export function availableOptions(
  loaded: LoadedOptions,
  {
    brandScope,
    adsStyle,
    emailStyle,
  }: { brandScope: BrandScope } & Pick<
    ComposerSelections,
    "adsStyle" | "emailStyle"
  >,
): ComposerOptions {
  const styles = (surfaces: readonly AgentScope[], selection: StyleSelection) =>
    loaded.styles
      .filter(
        (style) =>
          isStyleOffered(
            style,
            surfaces,
            selection.kind === "style" ? selection.id : undefined,
          ) && isInBrandScope(style.brandProfileIds, brandScope),
      )
      .sort(newestFirst);
  return {
    adStyles: styles(AD_SURFACES, adsStyle),
    emailStyles: styles(EMAIL_SURFACES, emailStyle),
    adTemplates: loaded.adTemplates
      .filter(
        (template) =>
          isAdTemplateOffered(template) &&
          isInBrandScope(template.brandProfileIds, brandScope),
      )
      .sort(newestFirst),
    skills: loaded.skills.filter(
      (skill) =>
        isSkillOffered(skill) &&
        isInBrandScope(skill.brandProfileIds, brandScope),
    ),
  };
}

// The agent can read only the published workspace skills its session staged.
function isSkillOffered(skill: SkillRow): boolean {
  return skill.isActive && !skill.isManaged && !skill.workspaceDisabled;
}

function newestFirst(a: { updatedAt: string }, b: { updatedAt: string }) {
  return b.updatedAt.localeCompare(a.updatedAt);
}
